/**
 * Field Mapping Viewer
 * 
 * เครื่องมือสำหรับดูและเทียบ Field Mapping ระหว่าง UI และ Parse
 * ใช้ก่อนเชื่อมต่อจริงเพื่อตรวจสอบความถูกต้อง
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Code,
  Database,
  ArrowRight,
  Check,
  X,
  Search,
  Copy,
  Download,
  RefreshCcw,
} from 'lucide-react';
import {
  memberFieldMap,
  transactionFieldMap,
  slipFieldMap,
  bankAccountFieldMap,
} from '@/services/field-mappings';
import { FieldMap } from '@/services/field-mappings/types';
import { generateFieldMapDoc, mapFromParse, validateParseObject } from '@/services/utils/field-mapper';

// Available mappings
const mappings: Record<string, { name: string; map: FieldMap }> = {
  member: { name: 'Member', map: memberFieldMap },
  transaction: { name: 'Transaction', map: transactionFieldMap },
  slip: { name: 'Slip', map: slipFieldMap },
  bankAccount: { name: 'Bank Account', map: bankAccountFieldMap },
};

export function FieldMappingViewer() {
  const [selectedMapping, setSelectedMapping] = useState('member');
  const [selectedVersion, setSelectedVersion] = useState<'v1' | 'v2'>('v2');
  const [searchTerm, setSearchTerm] = useState('');
  const [testJson, setTestJson] = useState('');
  const [testResult, setTestResult] = useState<{
    mapped: Record<string, unknown> | null;
    validation: { valid: boolean; errors: string[] } | null;
  }>({ mapped: null, validation: null });

  const currentMap = mappings[selectedMapping].map;
  
  // Filter fields by search
  const filteredFields = Object.entries(currentMap).filter(([uiField, mapping]) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      uiField.toLowerCase().includes(search) ||
      mapping.parseV1Field.toLowerCase().includes(search) ||
      mapping.parseV2Field.toLowerCase().includes(search)
    );
  });

  // Test JSON mapping
  const handleTestMapping = () => {
    try {
      const parseObj = JSON.parse(testJson);
      const validation = validateParseObject(parseObj, currentMap, selectedVersion);
      const mapped = mapFromParse(parseObj, currentMap, selectedVersion);
      
      setTestResult({ mapped: mapped as Record<string, unknown> | null, validation });
    } catch (e) {
      setTestResult({
        mapped: null,
        validation: { valid: false, errors: [(e as Error).message] },
      });
    }
  };

  // Copy markdown documentation
  const handleCopyDoc = () => {
    const doc = generateFieldMapDoc(currentMap);
    navigator.clipboard.writeText(doc);
  };

  // Export as JSON
  const handleExportJson = () => {
    const exportData = {
      entity: selectedMapping,
      version: selectedVersion,
      fields: Object.entries(currentMap).map(([uiField, mapping]) => ({
        uiField,
        parseField: selectedVersion === 'v1' ? mapping.parseV1Field : mapping.parseV2Field,
        required: mapping.required || false,
        defaultValue: mapping.defaultValue,
        hasTransform: !!mapping.fromParse || !!mapping.toParse,
      })),
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `field-mapping-${selectedMapping}-${selectedVersion}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6 text-primary" />
            Field Mapping Viewer
          </h1>
          <p className="text-muted-foreground">เทียบ Field ระหว่าง UI และ Parse Server</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleCopyDoc} className="gap-2">
            <Copy className="h-4 w-4" />
            Copy Doc
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportJson} className="gap-2">
            <Download className="h-4 w-4" />
            Export JSON
          </Button>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Entity</label>
              <Select value={selectedMapping} onValueChange={setSelectedMapping}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(mappings).map(([key, { name }]) => (
                    <SelectItem key={key} value={key}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Parse Version</label>
              <Select value={selectedVersion} onValueChange={(v) => setSelectedVersion(v as 'v1' | 'v2')}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="v1">V1 (Legacy)</SelectItem>
                  <SelectItem value="v2">V2 (Current)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 space-y-1">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search fields..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="mapping">
        <TabsList>
          <TabsTrigger value="mapping">Field Mapping</TabsTrigger>
          <TabsTrigger value="test">Test Mapping</TabsTrigger>
        </TabsList>

        {/* Mapping Table */}
        <TabsContent value="mapping">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {mappings[selectedMapping].name} Field Mapping
              </CardTitle>
              <CardDescription>
                {filteredFields.length} fields • Parse {selectedVersion.toUpperCase()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">UI Field</TableHead>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Parse {selectedVersion.toUpperCase()} Field</TableHead>
                    <TableHead className="w-[100px]">Required</TableHead>
                    <TableHead className="w-[120px]">Default</TableHead>
                    <TableHead className="w-[100px]">Transform</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredFields.map(([uiField, mapping]) => {
                    const parseField = selectedVersion === 'v1' ? mapping.parseV1Field : mapping.parseV2Field;
                    const hasTransform = !!mapping.fromParse || !!mapping.toParse;
                    
                    return (
                      <TableRow key={uiField}>
                        <TableCell>
                          <code className="text-sm bg-muted px-2 py-1 rounded">{uiField}</code>
                        </TableCell>
                        <TableCell>
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        </TableCell>
                        <TableCell>
                          <code className="text-sm bg-primary/10 text-primary px-2 py-1 rounded">
                            {parseField}
                          </code>
                        </TableCell>
                        <TableCell>
                          {mapping.required ? (
                            <Badge variant="secondary" className="bg-amber-500/10 text-amber-500 border-0">
                              Required
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {mapping.defaultValue !== undefined ? (
                            <code className="text-xs">{JSON.stringify(mapping.defaultValue)}</code>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {hasTransform ? (
                            <Badge variant="secondary" className="bg-purple-500/10 text-purple-500 border-0">
                              <Code className="h-3 w-3 mr-1" />
                              Yes
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Test Mapping */}
        <TabsContent value="test">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Parse Object (Input)</CardTitle>
                <CardDescription>วาง JSON จาก Parse Server เพื่อทดสอบ</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <textarea
                  className="w-full h-64 font-mono text-sm p-3 rounded-lg border bg-muted/30"
                  placeholder={`{\n  "objectId": "abc123",\n  "username": "user001",\n  ...\n}`}
                  value={testJson}
                  onChange={(e) => setTestJson(e.target.value)}
                />
                <Button onClick={handleTestMapping} className="w-full gap-2">
                  <RefreshCcw className="h-4 w-4" />
                  Test Mapping
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">UI Model (Output)</CardTitle>
                <CardDescription>ผลลัพธ์หลังจาก Map เป็น UI Model</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {testResult.validation && (
                  <div className={`p-3 rounded-lg ${testResult.validation.valid ? 'bg-win/10' : 'bg-loss/10'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {testResult.validation.valid ? (
                        <>
                          <Check className="h-4 w-4 text-win" />
                          <span className="font-medium text-win">Validation Passed</span>
                        </>
                      ) : (
                        <>
                          <X className="h-4 w-4 text-loss" />
                          <span className="font-medium text-loss">Validation Failed</span>
                        </>
                      )}
                    </div>
                    {testResult.validation.errors.length > 0 && (
                      <ul className="text-sm text-loss space-y-1">
                        {testResult.validation.errors.map((err, i) => (
                          <li key={i}>• {err}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                
                {testResult.mapped && (
                  <pre className="w-full h-48 overflow-auto font-mono text-sm p-3 rounded-lg border bg-muted/30">
                    {JSON.stringify(testResult.mapped, null, 2)}
                  </pre>
                )}
                
                {!testResult.mapped && !testResult.validation && (
                  <div className="h-48 flex items-center justify-center text-muted-foreground">
                    วาง JSON และกด Test Mapping
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}








